import type { TSummaryGroupValues } from "@src/components/modules/projects/detail/Evaluation/types";
import {
  type ExpenseBudgetItemModel,
  type ExpenseStructuredModel,
  OurWorkBudgetItemModel,
  OurWorkOverviewModel,
} from "@src/components/modules/projects/detail/models";
import { currency, HUNDRED_PERCENT, percent } from "@src/utils/formatters";

/**
 *
 * @param value
 * @returns Formatted currency based on workspace
 */
export function formatCurrency(value: number): string {
  return currency.formatByWorkspace(value);
}

/**
 *
 * @param budgetItems
 * @returns Profit sum of all expense budget items
 */
export function getProfit(budgetItems: ExpenseBudgetItemModel[]): number {
  return budgetItems.reduce((prev, { stats }) => {
    return prev + stats.profit;
  }, 0);
}

/**
 *
 * @param budgetItems
 * @returns Average of profit percent of all expense budget items
 */
export function getProfitAvgPercent(
  budgetItems: ExpenseBudgetItemModel[],
): number {
  const itemCount = budgetItems.length;

  if (itemCount === 0) return 0;

  const percentSum = budgetItems.reduce((prev, { stats }) => {
    return prev + stats.profit_percent;
  }, 0);

  return percentSum / itemCount;
}

/**
 *
 * @param percent
 * @returns One hundredth of passed value, because formatted percent returns string with % at the end
 */
export function getRealPercent(percent: number): number {
  return percent * 0.01;
}

export function getSummaryGroupValues(
  data: OurWorkOverviewModel | ExpenseStructuredModel,
): TSummaryGroupValues {
  const itemCount = data.budgetItems.length;
  let netProfit = 0;
  let netMargin = 0;

  if (data instanceof OurWorkOverviewModel) {
    netProfit = data.budgetItems.reduce((prev, { stats }) => {
      return prev + stats.net_profit;
    }, 0);

    const sumInvoiced = data.budgetItems.reduce((prev, { stats }) => {
      return prev + stats.credit;
    }, 0);

    netMargin = itemCount > 0 ? (netProfit / sumInvoiced) * HUNDRED_PERCENT : 0;
  } else {
    netProfit = data.budgetItems.reduce((prev, { stats }) => {
      return prev + stats.profit;
    }, 0);

    const sumInvoiced = data.budgetItems.reduce((prev, { stats }) => {
      return prev + stats.credit;
    }, 0);

    netMargin = itemCount > 0 ? (netProfit / sumInvoiced) * HUNDRED_PERCENT : 0;
  }

  return {
    netProfit,
    netMargin,
  };
}

export function getSummaryGroupItemValues(
  item: OurWorkBudgetItemModel | ExpenseBudgetItemModel,
): TSummaryGroupValues {
  let netProfit = 0;
  let netMargin = 0;
  let efficiency: string | undefined;
  let negativeEfficiency: boolean | undefined;

  if (item instanceof OurWorkBudgetItemModel) {
    netProfit = item.stats.net_profit;
    netMargin = item.stats.net_margin_percent;
    efficiency =
      percent.format(item.stats.efficiency_percent) +
      " (" +
      formatCurrency(item.stats.remaining_budget) +
      ")";
    negativeEfficiency = item.stats.remaining_budget < 0;
  } else {
    netProfit = item.stats.profit;
    netMargin = item.stats.profit_percent;
  }

  return { netProfit, netMargin, efficiency, negativeEfficiency };
}
